import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["orientation", "timeout", "hideTimeout"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useControlledState } from 'ariakit-utils/hooks';
import { applyState } from 'ariakit-utils/misc';
import { useStore, useStorePublisher } from 'ariakit-utils/store';
import { useCompositeState } from '../composite/composite-state.js';
import { useHovercardState } from '../hovercard/hovercard-state.js';
import { M as MenuContext, a as MenuBarContext } from '../__utils-07f4a93f.js';
/**
 * Provides state for the `Menu` components.
 * @example
 * ```jsx
 * const menu = useMenuState({ placement: "top" });
 * <MenuButton state={menu}>Edit</MenuButton>
 * <Menu state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */

function useMenuState(_temp) {
  var _timeout;

  var _ref = _temp === void 0 ? {} : _temp,
      _ref$orientation = _ref.orientation,
      orientation = _ref$orientation === void 0 ? "vertical" : _ref$orientation,
      timeout = _ref.timeout,
      _ref$hideTimeout = _ref.hideTimeout,
      hideTimeout = _ref$hideTimeout === void 0 ? 0 : _ref$hideTimeout,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState("container"),
      _useState2 = _slicedToArray(_useState, 2),
      initialFocus = _useState2[0],
      setInitialFocus = _useState2[1];

  var _useControlledState = useControlledState(props.defaultValues || {}, props.values, props.setValues),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      values = _useControlledState2[0],
      setValues = _useControlledState2[1];

  var parentMenu = useStore(MenuContext, ["orientation", "hideAll"]);
  var parentMenuBar = useStore(MenuBarContext, ["orientation"]);
  var contextOrientation = (parentMenu == null ? void 0 : parentMenu.orientation) || (parentMenuBar == null ? void 0 : parentMenuBar.orientation);
  var parentIsMenuBar = !!parentMenuBar && !parentMenu; // Defines the placement of the menu popover based on the parent orientation.

  var placement = props.placement || (contextOrientation === "vertical" ? "right-start" : "bottom-start");
  timeout = ((_timeout = timeout) != null ? _timeout : parentIsMenuBar) ? 0 : 150;
  var composite = useCompositeState(_objectSpread({
    orientation: orientation
  }, props));
  var hovercard = useHovercardState(_objectSpread(_objectSpread({
    timeout: timeout,
    hideTimeout: hideTimeout
  }, props), {}, {
    placement: placement
  })); // Resets the initial focus state when the menu is closed.

  useEffect(function () {
    if (hovercard.mounted) return;
    composite.setActiveId(null);
  }, [hovercard.mounted, composite.setActiveId]);
  var setValue = useCallback(function (name, value) {
    // Preventing prototype pollution.
    if (name === "__proto__" || name === "constructor") return;
    setValues(function (prevValues) {
      var prevValue = prevValues[name];
      var nextValue = applyState(value, prevValue);

      if (nextValue === prevValue) {
        return prevValues;
      }

      return _objectSpread(_objectSpread({}, prevValues), {}, _defineProperty({}, name, nextValue === undefined ? !!nextValue : nextValue));
    });
  }, [setValues]);
  var hideAll = useCallback(function () {
    hovercard.hide();
    parentMenu == null ? void 0 : parentMenu.hideAll();
  }, [hovercard.hide, parentMenu == null ? void 0 : parentMenu.hideAll]);
  var state = useMemo(function () {
    return _objectSpread(_objectSpread(_objectSpread({}, composite), hovercard), {}, {
      initialFocus: initialFocus,
      setInitialFocus: setInitialFocus,
      values: values,
      setValues: setValues,
      setValue: setValue,
      hideAll: hideAll
    });
  }, [composite, hovercard, initialFocus, setInitialFocus, values, setValues, setValue, hideAll]);
  return useStorePublisher(state);
}

export { useMenuState };